import { CheckCircle, Shield, Clock, Book, Search, MessageSquare } from "lucide-react"
import Image from "next/image"
import { cn } from "@/lib/utils"

export function WhyCareAdvisorWorks() {
  const features = [
    {
      icon: <Shield className="h-5 w-5" />,
      title: "Authoritative Sources",
      description: "Direct insights from Minnesota state regulations and official government websites"
    },
    {
      icon: <Clock className="h-5 w-5" />,
      title: "Time-Saving Research",
      description: "Find compliance answers in seconds instead of hours of manual research"
    },
    {
      icon: <Search className="h-5 w-5" />,
      title: "Precise References",
      description: "Every answer includes exact citations to relevant Minnesota statutes"
    },
    {
      icon: <Book className="h-5 w-5" />,
      title: "Minnesota-Specific",
      description: "Tailored exclusively for Minnesota healthcare compliance requirements"
    }
  ]

  return (
    <section className="relative py-24 md:py-32 bg-gradient-to-b from-white to-gray-50 dark:from-[#0F172A] dark:to-[#1E293B] overflow-hidden">
      {/* Curved section top divider */}
      <div className="absolute top-0 inset-x-0 h-16">
        <svg className="absolute inset-0 h-full w-full text-white dark:text-[#0F172A]" preserveAspectRatio="none" viewBox="0 0 1440 80" fill="currentColor">
          <path d="M0,0V80H1440V0C1440,0,1320,60,720,60C120,60,0,0,0,0Z" />
        </svg>
      </div>

      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient orbs */}
        <div className="absolute top-1/4 -left-20 w-60 h-60 bg-primary/10 dark:bg-primary/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 right-0 w-80 h-80 bg-primary-light/10 dark:bg-primary-light/20 rounded-full blur-3xl"></div>
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-grid-gray-500/[0.03] dark:bg-grid-gray-400/[0.05] bg-[length:30px_30px]"></div>
      </div>

      <div className="container px-4 md:px-8 lg:px-12 mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
          <p className="text-primary dark:text-primary-light uppercase tracking-wider font-medium mb-3">
            WHY CAREADVISOR WORKS
          </p>
          <h2 className="text-4xl md:text-5xl xl:text-6xl font-bold text-foreground dark:text-foreground mb-6 tracking-tight">
            Trusted <span className="text-primary dark:text-primary-light">Sources</span>
          </h2>
          <p className="text-xl text-muted-foreground dark:text-muted-foreground max-w-2xl mx-auto">
            Direct access to Minnesota regulations and guidelines from official government sources, saving you hours of research time.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Features section */}
          <div className="space-y-10">
            <div className="relative">
              {/* Subtle highlight behind key metrics */}
              <div className="absolute -inset-4 bg-primary/5 dark:bg-primary/10 rounded-2xl blur-lg"></div>
              
              {/* Key metrics */}
              <div className="relative grid grid-cols-2 gap-6 mb-12">
                <div className="bg-white dark:bg-[#1E293B] shadow-lg rounded-xl p-6 border border-border/50 transform transition-all hover:scale-105 duration-300">
                  <div className="text-4xl md:text-5xl font-bold text-primary dark:text-primary-light mb-2">10x</div>
                  <div className="text-sm text-muted-foreground dark:text-muted-foreground">Faster than manual research</div>
                </div>
                <div className="bg-white dark:bg-[#1E293B] shadow-lg rounded-xl p-6 border border-border/50 transform transition-all hover:scale-105 duration-300">
                  <div className="text-4xl md:text-5xl font-bold text-primary dark:text-primary-light mb-2">100%</div>
                  <div className="text-sm text-muted-foreground dark:text-muted-foreground">Official sources</div>
                </div>
              </div>
            </div>

            {/* Feature list */}
            <div className="space-y-6">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className={cn(
                    "bg-white dark:bg-[#1E293B] border border-border/50 rounded-xl p-5 shadow-md flex gap-4 transform transition-all hover:-translate-y-1 hover:shadow-lg duration-300",
                    index % 2 === 0 ? "lg:translate-x-0" : "lg:translate-x-8"
                  )}
                >
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-primary-light">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-foreground dark:text-foreground mb-1">{feature.title}</h3>
                    <p className="text-muted-foreground dark:text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chat interface - replacing dashboard visualization */}
          <div className="relative">
            {/* Glowing background effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-primary-light/20 dark:from-primary/30 dark:to-primary-light/30 rounded-3xl blur-xl"></div>
            
            {/* Main chat card */}
            <div className="relative bg-white dark:bg-[#1E293B] rounded-xl shadow-2xl border border-border dark:border-border overflow-hidden transform transition-all hover:scale-[1.02] duration-500">
              {/* Chat header */}
              <div className="bg-gray-50 dark:bg-[#131E31] px-6 py-4 border-b border-border/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-primary dark:text-primary-light" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground dark:text-foreground">CareAdvisor Chat</h3>
                    <p className="text-xs text-muted-foreground dark:text-muted-foreground">Powered by Minnesota regulatory data</p>
                  </div>
                </div>
                <span className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 text-xs font-medium px-3 py-1 rounded-full">LIVE</span>
              </div>

              {/* Chat content */}
              <div className="p-5 max-h-96 overflow-y-auto bg-gray-50 dark:bg-[#131E31]">
                {/* User question */}
                <div className="flex justify-end mb-4">
                  <div className="bg-primary/10 dark:bg-primary/20 rounded-2xl rounded-tr-none px-4 py-3 text-foreground dark:text-white max-w-md">
                    <p>What is the difference between a 245D license and a Home Care license?</p>
                  </div>
                </div>
                
                {/* AI response */}
                <div className="flex mb-4">
                  <div className="bg-white dark:bg-gray-800 rounded-2xl rounded-tl-none px-4 py-3 text-foreground dark:text-gray-200 max-w-md">
                    <p className="mb-3">The differences between a 245D license and a Home Care license in Minnesota are:</p>
                    
                    <ul className="list-disc pl-5 space-y-2 mb-3 text-sm">
                      <li><span className="font-semibold">Regulating Agency:</span> 245D is regulated by DHS, while Home Care is regulated by MDH.</li>
                      <li><span className="font-semibold">Focus:</span> 245D focuses on HCBS for individuals with disabilities, while Home Care covers both medical and non-medical services.</li>
                      <li><span className="font-semibold">Target Population:</span> 245D primarily serves Medicaid waiver recipients, while Home Care serves the general population.</li>
                    </ul>
                    
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-muted-foreground dark:text-gray-400 mb-1 font-medium">Sources:</p>
                      <ul className="text-xs space-y-1 text-primary/80 dark:text-primary-light/80">
                        <li>• MN Statute 245D.03 - Home and Community-Based Services</li>
                        <li>• MN Statute 144A.471 - Home Care License Requirements</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* User follow-up */}
                <div className="flex justify-end mb-4">
                  <div className="bg-primary/10 dark:bg-primary/20 rounded-2xl rounded-tr-none px-4 py-3 text-foreground dark:text-white max-w-md">
                    <p>What are the fee differences?</p>
                  </div>
                </div>
                
                {/* AI follow-up response */}
                <div className="flex">
                  <div className="bg-white dark:bg-gray-800 rounded-2xl rounded-tl-none px-4 py-3 text-foreground dark:text-gray-200 max-w-md">
                    <p>For license fees in Minnesota:</p>
                    
                    <ul className="list-disc pl-5 space-y-1 mb-3 text-sm">
                      <li><span className="font-semibold">245D License:</span> Fees vary by service type and volume</li>
                      <li><span className="font-semibold">Home Care License:</span> $2,100 for Basic and $4,200 for Comprehensive</li>
                    </ul>
                    
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-muted-foreground dark:text-gray-400 mb-1 font-medium">Sources:</p>
                      <ul className="text-xs space-y-1 text-primary/80 dark:text-primary-light/80">
                        <li>• MN DHS Fee Schedule (Updated 2023)</li>
                        <li>• MN MDH Home Care Provider Licensing</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Input area */}
              <div className="border-t border-border/50 p-4 bg-white dark:bg-[#131E31] flex items-center gap-3">
                <div className="flex-1 bg-gray-100 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-full px-4 py-2 text-sm text-gray-400 dark:text-gray-500">
                  Type your compliance question...
                </div>
                <button className="h-9 w-9 rounded-full bg-primary hover:bg-primary/90 text-white flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-up-right">
                    <path d="M7 7h10v10" />
                    <path d="M7 17 17 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Curved section bottom divider */}
      <div className="absolute bottom-0 inset-x-0 h-16">
        <svg className="absolute inset-0 h-full w-full text-gray-50 dark:text-[#1E293B]" preserveAspectRatio="none" viewBox="0 0 1440 80" fill="currentColor">
          <path d="M1440,80V0H0V80C0,80,120,20,720,20C1320,20,1440,80,1440,80Z" />
        </svg>
      </div>
    </section>
  )
} 